import React, { useState } from 'react';
import { Package, BarChart3, TrendingUp, ShoppingCart, ClipboardCheck } from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { StockMovementReportView } from './StockMovementReportView';
import { StockOverviewReportView } from './StockOverviewReportView';
import { StockAnalyticsReportView } from './StockAnalyticsReportView';
import { PurchasePlanningReportView } from './PurchasePlanningReportView';
import { StockAdjustmentsAuditView } from './StockAdjustmentsAuditView';
import { clsx } from 'clsx';

type TabId = 'movements' | 'overview' | 'analytics' | 'purchasePlanning' | 'adjustmentsAudit';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  permission: string;
}

const TABS: Tab[] = [
  { id: 'movements', label: 'Movimentações de Estoque', icon: <BarChart3 size={16} />, permission: 'reports.stock_movements' },
  { id: 'overview', label: 'Visão Geral do Estoque', icon: <Package size={16} />, permission: 'reports.stock_overview' },
  { id: 'analytics', label: 'Análise de Estoque', icon: <TrendingUp size={16} />, permission: 'reports.stock_analytics' },
  { id: 'purchasePlanning', label: 'Planejamento de Compras', icon: <ShoppingCart size={16} />, permission: 'reports.purchase_planning' },
  // admin, finance e logistics_operator (RN-EST-76)
  { id: 'adjustmentsAudit', label: 'Auditoria de Ajustes', icon: <ClipboardCheck size={16} />, permission: 'reports.stock_adjustments_audit' },
];

export const StockReportsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabId>('movements');

  // Filter tabs by permission
  const availableTabs = TABS.filter(tab => checkPermission(tab.permission).hasPermission);

  // If current active tab is not available, switch to first available
  const effectiveTab = availableTabs.find(t => t.id === activeTab) ? activeTab : (availableTabs[0]?.id ?? 'movements');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Estoque</h1>
        <p className="text-sm text-slate-500 mt-1">Relatórios e visão geral do estoque</p>
      </div>

      {/* Tabs */}
      {availableTabs.length > 1 && (
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px gap-0" aria-label="Abas do relatório de estoque">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'inline-flex items-center gap-2 whitespace-nowrap py-3 px-5 text-sm font-medium border-b-2 transition-colors',
                  effectiveTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      {effectiveTab === 'movements' && <StockMovementReportView />}
      {effectiveTab === 'overview' && <StockOverviewReportView />}
      {effectiveTab === 'analytics' && <StockAnalyticsReportView />}
      {effectiveTab === 'purchasePlanning' && <PurchasePlanningReportView />}
      {effectiveTab === 'adjustmentsAudit' && <StockAdjustmentsAuditView />}
    </div>
  );
};

export default StockReportsView;
